import { initializeApp, getApps } from "firebase/app";
import { getAuth } from "firebase/auth";
import {
  getMessaging,
  getToken,
  isSupported,
  type Messaging,
} from "firebase/messaging";
import { initializeAppCheck, ReCaptchaV3Provider } from "firebase/app-check";

const firebaseConfig = {
  apiKey: process.env.NEXT_PUBLIC_FIREBASE_API_KEY,
  authDomain: `${process.env.NEXT_PUBLIC_FIREBASE_PROJECT_ID}.firebaseapp.com`,
  projectId: process.env.NEXT_PUBLIC_FIREBASE_PROJECT_ID,
  storageBucket: `${process.env.NEXT_PUBLIC_FIREBASE_PROJECT_ID}.appspot.com`,
  messagingSenderId: process.env.NEXT_PUBLIC_FIREBASE_SENDER_ID,
  appId: process.env.NEXT_PUBLIC_FIREBASE_APP_ID,
};

const app = !getApps().length ? initializeApp(firebaseConfig) : getApps()[0];

if (typeof window !== "undefined") {
  try {
    initializeAppCheck(app, {
      provider: new ReCaptchaV3Provider(
        process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY!,
      ),
      isTokenAutoRefreshEnabled: true,
    });
  } catch (err) {
    console.warn("App Check failed to initialize:", err);
  }
}

const auth = getAuth(app);

const registerServiceWorker =
  async (): Promise<ServiceWorkerRegistration | null> => {
    if (typeof window === "undefined" || !("serviceWorker" in navigator)) {
      console.warn("Service workers are not supported in this environment.");
      return null;
    }

    try {
      const registration = await navigator.serviceWorker.register(
        "/firebase-messaging-sw.js",
      );
      return registration;
    } catch (error) {
      console.error("Service Worker registration failed:", error);
      return null;
    }
  };

const requestNotificationPermission =
  async (): Promise<NotificationPermission> => {
    if (typeof window === "undefined" || !("Notification" in window)) {
      console.warn("Notifications are not supported in this browser.");
      return "denied";
    }

    try {
      const permission = await Notification.requestPermission();
      return permission;
    } catch (error) {
      console.error("Error requesting notification permission:", error);
      return "denied";
    }
  };

const getFcmToken = async (): Promise<string | null> => {
  const supported = await isSupported();
  if (!supported) {
    console.warn("FCM not supported in this browser (e.g., Safari).");
    return null;
  }

  const registration = await registerServiceWorker();
  if (!registration) {
    console.error("Failed to register service worker. Cannot get FCM token.");
    return null;
  }

  if (Notification.permission !== "granted") {
    const permission = await requestNotificationPermission();
    if (permission !== "granted") {
      console.warn("Notification permission denied. Cannot get FCM token.");
      return null;
    }
  }

  let messaging: Messaging | null = null;
  if (typeof window !== "undefined" && "serviceWorker" in navigator) {
    try {
      messaging = getMessaging(app);
    } catch (err) {
      console.error("Messaging not supported in this environment:", err);
      return null;
    }
  }

  if (!messaging) {
    console.error("Failed to initialize Firebase Messaging.");
    return null;
  }

  try {
    const token = await getToken(messaging, {
      vapidKey: process.env.NEXT_PUBLIC_FIREBASE_VAPID_KEY,
      serviceWorkerRegistration: registration,
    });

    if (token) {
      console.log("FCM token generated successfully:", token);
      return token;
    } else {
      console.warn("No FCM token received.");
      return null;
    }
  } catch (error) {
    console.error("Failed to get FCM token:", error);
    return null;
  }
};

const getMessagingInstance = async (): Promise<Messaging | null> => {
  const supported = await isSupported();
  if (!supported) return null;
  if (typeof window === "undefined") return null;
  return getMessaging(app);
};

const isNotificationSupported = (): boolean => {
  if (typeof window === "undefined") return false;
  if (!("Notification" in window)) return false;
  if (!("serviceWorker" in navigator)) return false;
  return true;
};

const getFcmTokenIfGranted = async (): Promise<string | null> => {
  if (!isNotificationSupported()) return null;
  if (Notification.permission !== "granted") return null;

  const supported = await isSupported();
  if (!supported) return null;

  const registration = await registerServiceWorker();
  if (!registration) return null;

  let messaging: Messaging | null = null;
  try {
    messaging = getMessaging(app);
  } catch (err) {
    console.error("Messaging not supported in this environment:", err);
    return null;
  }

  if (!messaging) return null;

  try {
    const token = await getToken(messaging, {
      vapidKey: process.env.NEXT_PUBLIC_FIREBASE_VAPID_KEY,
      serviceWorkerRegistration: registration,
    });
    if (token) {
      console.log("FCM token retrieved (already granted):", token);
      return token;
    }
    return null;
  } catch (error) {
    console.error("Failed to get FCM token:", error);
    return null;
  }
};

export {
  auth,
  getFcmToken,
  getFcmTokenIfGranted,
  requestNotificationPermission,
  getMessagingInstance,
  isNotificationSupported,
};
